import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { ServerTransactionStatus } from '@wix/cashier-common/dist/src/enums/transactions/ServerTransactionStatus';
import {
  IChargeTokenResponse,
  RedirectTarget,
} from '@wix/cashier-core/dist/src/services/Payment/types';
import { EventEmitter } from 'events';
import { win } from '../../../utils/win';
import { ICashierSDKConfiguration } from '../../cashier-sdk';
import { PaymentMethodsEvents } from '../../types';
import { AsyncPaymentResult } from '../../types/AsyncPaymentResult';
import { MFieldsNested } from '../../types/MField';
import { PaymentCompleteResult } from '../../types/PaymentCompleteResult';
import { ClientStatus } from '../../types/PaymentStatus';
import { PaymentStartResult } from '../../types/PublicCallbacks';
import {
  createExpectedError,
  isExpectedError,
} from '../../utils/expectedError';
import { getRedirectTargetInfo } from '../../utils/getRedirectTargetInfo';
import { isPayModalEnabled } from '../../utils/isPayModalEnabled';
import { openPaymentInModal } from '../../utils/openPaymentInModal';
import { createPaymentCompleteResultBuilder } from '../../utils/paymentCompleteResult';
import { paymentPopup } from '../../utils/PaymentPopup';
import {
  getPaymentPopupSize,
  parseReturnUrlQueryParams,
} from '../../utils/PaymentPopup/utils';
import { ServerErrorCode } from '../../utils/serverErrorMapping';

const EVENT_POPUP_OPEN = 'payment:popup:open';
const EVENT_POPUP_CLOSE = 'payment:popup:close';
const EVENT_PAYMENT_FAILED = 'payment:failed';

export const events = { EVENT_POPUP_OPEN, EVENT_POPUP_CLOSE };

class PaymentModule {
  constructor(
    private sdkInitConfig?: ICashierSDKConfiguration,
    private eventEmitter?: EventEmitter
  ) {}

  public getRedirectTarget = (params: {
    paymentMethod: PaymentMethod;
    frameable: boolean;
  }): RedirectTarget =>
    getRedirectTargetInfo(this.sdkInitConfig, params).redirectTarget;

  public emit = (event: string, ...args: any[]) => {
    this.eventEmitter?.emit.apply(this.eventEmitter, [event, ...args]);
    return this;
  };

  public preOpenPaymentPopup = (paymentMethod: PaymentMethod) => {
    if (paymentPopup.isOpened()) {
      throw new Error(
        `Another payment is prepared/in progress. Call "cancelPayment" first before starting the new one`
      );
    }

    try {
      // pre-opening is needed because new tab/win can be opened only as a sync reaction to the user action (e.g. in click handler)
      // after we get charge response we'll reuse this pre-opened tab/win and navigate it to url from the response
      paymentPopup.open({
        url: this.sdkInitConfig.processingPageUrl,
        sizes: getPaymentPopupSize(paymentMethod),
      });
    } catch (error) {
      throw isExpectedError(error)
        ? error
        : createExpectedError({
            errorMessage: `Failed to pre-open payment popup`,
            errorData: error,
          });
    }

    try {
      paymentPopup.listenClose(() => {
        this.emit(EVENT_POPUP_CLOSE);
      });

      this.emit(EVENT_POPUP_OPEN);
    } catch (error) {
      // can be ignored, not crucial for the flow
      console.error(`Failed to start listening to payment popup close`, error);
    }
  };

  public navigatePaymentPopupTo = (
    providerPageUrl: string
  ): Promise<AsyncPaymentResult> => {
    return new Promise<AsyncPaymentResult>((resolve) => {
      paymentPopup.listenPaymentFinish((result) => {
        paymentPopup.close(false);
        resolve(result);
      });

      paymentPopup.listenClose((isPaymentAborted) => {
        if (isPaymentAborted) {
          resolve({
            // TODO: revise what we should return in this case DECLINED or PENDING
            status: ServerTransactionStatus.Declined,
            failureCode: ServerErrorCode.BuyerCanceled,
          });
        }
      });

      paymentPopup.redirectTo(providerPageUrl);
    });
  };

  public closePaymentPopup = () => {
    paymentPopup.close(true);
  };

  public openPaymentInCurrentTab = (url: string) => {
    if (win.location || win.top?.location) {
      if (win.top?.location) {
        win.top.location.href = url;
      } else {
        win.location.href = url;
      }
    } else {
      console.error('No location provided');
    }
  };

  public squareVerifyBuyer = (cardToken: string): Promise<string> =>
    new Promise((resolve, reject) => {
      this.sdkInitConfig.squareVerifyBuyer({
        cardToken,
        onComplete: resolve,
        onManualClose: () => {
          reject(
            createExpectedError({
              serverErrorCode: ServerErrorCode.BuyerCanceled,
              errorMessage: `Square 3DS modal was manually closed`,
            })
          );
        },
        onError: (error?: any) => {
          reject(
            createExpectedError({
              serverErrorCode: ServerErrorCode.Failed3DS,
              errorMessage: `Square 3DS challenge failed`,
              errorData: error,
            })
          );
        },
      });
    });

  private handlePendingPayment = async (
    paymentMethod: PaymentMethod,
    chargeResponse: IChargeTokenResponse
  ): Promise<AsyncPaymentResult | undefined> => {
    if (!this.sdkInitConfig.handlePendingPayment) {
      return;
    }

    return new Promise((resolve, reject) => {
      this.sdkInitConfig.handlePendingPayment({
        paymentMethod,
        chargeResponse,
        onComplete: resolve,
        onManualClose: () => {
          resolve({
            status: ServerTransactionStatus.BuyerCanceled,
            failureCode: ServerErrorCode.BuyerCanceled,
          });
        },
        onTimeout: () => {
          resolve({
            status: ServerTransactionStatus.Timeout,
            failureCode: '3035',
          });
        },
        onError: (error: any) => {
          reject(
            createExpectedError({
              errorMessage: `Failed to handle pending transaction`,
              errorData: error,
            })
          );
        },
      });
    });
  };

  public continuePayment = (
    paymentMethod: PaymentMethod,
    params: {
      chargeResponse: IChargeTokenResponse;
      inNewTab?: boolean;
      saveCreditCard?: boolean;
      instance?: string;
      creditCardId?: string;
      frameable?: boolean;
      responsive?: boolean;
      mFieldsNested?: MFieldsNested;
    }
  ): Promise<PaymentCompleteResult> => {
    return new Promise<PaymentCompleteResult>(async (resolve, reject) => {
      const {
        chargeResponse: initialChargeResponse,
        inNewTab,
        saveCreditCard,
        creditCardId,
        frameable,
        responsive,
      } = params;
      let chargeResponse: IChargeTokenResponse = initialChargeResponse;

      if (this.sdkInitConfig.onPaymentStart) {
        const paymentStartParams: PaymentStartResult = {
          type: PaymentMethodsEvents.PAYMENT_START,
          chargeId: chargeResponse.chargeId,
        };

        if (saveCreditCard !== undefined) {
          paymentStartParams.isSaveCreditCard = Boolean(saveCreditCard);
        }

        if (creditCardId) {
          paymentStartParams.creditCardId = creditCardId;
        }

        const redirectTarget = this.getRedirectTarget({
          paymentMethod,
          frameable,
        });

        if (redirectTarget) {
          paymentStartParams.redirectTarget = redirectTarget;

          if (
            getRedirectTargetInfo(this.sdkInitConfig, {
              paymentMethod,
              frameable,
            }).forcedFullRedirectInIAB
          ) {
            paymentStartParams.forcedFullRedirectInIAB = true;
          }
        }

        this.sdkInitConfig.onPaymentStart(paymentMethod, paymentStartParams);
      }

      const paymentResultBuilder = createPaymentCompleteResultBuilder()
        .withPaymentMethod(paymentMethod)
        .withChargeInitiated()
        .withChargeResponse(chargeResponse);
      const getClientStatus = (): ClientStatus =>
        paymentResultBuilder.build().clientStatus;

      if (getClientStatus() === 'InProcess') {
        const providerPageUrl = chargeResponse.redirectUrl;
        const shouldPayUsingModal =
          frameable &&
          !!this.sdkInitConfig.openPayModal &&
          isPayModalEnabled(this.sdkInitConfig.appId);

        if (!shouldPayUsingModal && !inNewTab) {
          resolve(paymentResultBuilder.build());
          this.openPaymentInCurrentTab(providerPageUrl);
          return;
        }

        if (shouldPayUsingModal && paymentPopup.isOpened()) {
          // if for some reason the popup was pre-opened (e.g. client triggered it)
          // and we decide to use modal instead
          this.closePaymentPopup();
        }

        let asyncPaymentResult;

        if (shouldPayUsingModal) {
          asyncPaymentResult = await openPaymentInModal(
            this.sdkInitConfig.openPayModal,
            {
              paymentMethod,
              providerPageUrl,
              responsive,
            }
          );
        } else {
          if (!paymentPopup.isOpened()) {
            chargeResponse.transactionStatus =
              ServerTransactionStatus.BuyerCanceled;
            chargeResponse.failureCode = ServerErrorCode.BuyerCanceled;
            paymentResultBuilder.withChargeResponse(chargeResponse);
            paymentResultBuilder.withInternalError(
              createExpectedError({
                errorMessage: `Failed to open PSP page - no pre-opened popup found to navigate in. Most likely it was closed by the User.`,
              })
            );
            reject(paymentResultBuilder.build());
            return;
          }

          asyncPaymentResult = await this.navigatePaymentPopupTo(
            providerPageUrl
          );
        }

        if (!asyncPaymentResult.failureCode && asyncPaymentResult.returnUrl) {
          const failureCodeFromReturnUrl = parseReturnUrlQueryParams(
            asyncPaymentResult.returnUrl
          )?.failureCode;

          if (failureCodeFromReturnUrl) {
            asyncPaymentResult.failureCode = failureCodeFromReturnUrl;
          }
        }

        chargeResponse = {
          ...chargeResponse,
          transactionStatus: asyncPaymentResult.status,
          failureCode: asyncPaymentResult.failureCode,
          redirectUrl: asyncPaymentResult.returnUrl,
        };

        paymentResultBuilder.withChargeResponse(chargeResponse);
      }

      if (paymentPopup.isOpened()) {
        paymentPopup.close();
      }

      if (getClientStatus() === 'Pending') {
        let pendingPaymentResult: AsyncPaymentResult;
        try {
          pendingPaymentResult = await this.handlePendingPayment(
            paymentMethod,
            chargeResponse
          );
        } catch (e) {
          chargeResponse.transactionStatus = ServerTransactionStatus.Failed;
          paymentResultBuilder.withChargeResponse(chargeResponse);

          if (isExpectedError(e)) {
            paymentResultBuilder.withInternalError(e);
          } else {
            paymentResultBuilder.withUnexpectedInternalError(e);
          }
        }

        if (pendingPaymentResult) {
          chargeResponse = {
            ...chargeResponse,
            transactionStatus: pendingPaymentResult.status,
            failureCode: pendingPaymentResult.failureCode,
          };

          paymentResultBuilder.withChargeResponse(chargeResponse);
        }
      }

      if (['Approved', 'Pending'].includes(getClientStatus())) {
        resolve(paymentResultBuilder.build());
      } else {
        if (getClientStatus() === 'Unknown') {
          console.error(
            `Cashier SDK :: continuePayment was called with unknown transactionStatus ${chargeResponse.transactionStatus}`
          );

          paymentResultBuilder.withServerStatus(
            ServerTransactionStatus.Undefined
          );
        }

        const paymentCompleteResult = paymentResultBuilder.build();
        this.emit(EVENT_PAYMENT_FAILED, paymentCompleteResult);
        reject(paymentCompleteResult);
      }
    });
  };
}

export default PaymentModule;
